import React, { useState, useContext } from "react"
import styled, { css } from "styled-components"
import { Link, StaticQuery, graphql, navigate } from "gatsby"
import { useScrollPosition } from "@n8tb1t/use-scroll-position"
import clsx from "clsx"

import {
  NavQuery,
  ContentfulHeading,
  ParagraphWithRemarkFragment,
  ContentfulLink,
  ContentfulNavigation,
} from "../../types/graphql-types"
import { RootLayout, nav, padding, breakpoints as bp } from "./layout"
import { HmdConnectLogo } from "./logo"
import Button from "./forms/button"
import colors, { hexToRgba } from "../utils/colors"
import LocaleContext from "../context/localeContext"

import Icon from "./Icon"
import ExternalLinkButton from "./forms/externalLinkButton"
import BaseModal, { ModalStatus } from "./rootModal"
import Paragraph from "./paragraph"
import { GooglePlayCta, AppStoreCta, WebStoreCta } from "./downloadButtons"
import LanguageSelector from "./LanguageSelector"
import CurrencySelector from "./CurrencySelector"
import { createLocalizedPath } from "../utils/urlUtils"

interface Props {
  activeTab?: string
  isInverted?: boolean
}

interface NavProps extends Props {
  data: NavQuery
}

interface DesktopMenuBarProps {
  isFull: boolean
}

interface MobileMenuBarProps {
  isFull: boolean
}

interface ChangesOnMobile {
  isMobile?: boolean
}

const Container = styled(RootLayout)<{
  isTransparent?: boolean
  isInverted?: boolean
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  font-weight: bold;
  z-index: 10;
  font-size: 1rem;

  a.active {
    color: ${props => (props.isTransparent && props.isInverted ? colors.white : colors.black)};
  }

  background-color: ${props => (props.isTransparent ? "transparent" : colors.white)};
`

const DesktopContainer = styled(Container)`
  height: ${nav.desktop};

  @media (max-width: ${bp.tabletMax}) {
    display: none;
  }
`

const MobileContainer = styled(Container)<{ absolute?: boolean }>`
  padding: 0;
  height: ${nav.mobile};
  ${props => (props.absolute ? "position: absolute;" : "")}

  @media (min-width: ${bp.desktopMin}) {
    display: none;
  }

  @media (min-width: ${bp.tabletMin}) {
    padding: 0;
    height: ${nav.desktop};
  }
`

const SubContainerStretch = css`
  flex: 1;
`

const SubContainerNonStretch = css`
  width: 210px;

  @media (max-width: ${bp.tabletMax}) {
    width: 50px;
  }
`

const SubContainer = styled.div<{
  justifyContent?: string
  stretch?: boolean
  fixedWidth?: string
}>`
  display: flex;
  align-items: center;
  height: 100%;
  justify-content: ${props => (props.justifyContent ? props.justifyContent : "center")};

  @media (max-width: ${bp.tabletMax}) {
    padding: 0 ${padding.desktop};
  }
  @media (max-width: ${bp.mobileMax}) {
    padding: 0 ${padding.mobile};
  }

  ${props =>
    props.stretch
      ? SubContainerStretch
      : props.fixedWidth
      ? `width: ${props.fixedWidth};`
      : SubContainerNonStretch}
`

const TabletSubContainer = styled(SubContainer)`
  display: none;
  @media (min-width: ${bp.tabletMin}) and (max-width: ${bp.tabletMax}) {
    display: inherit;
  }
`

const MobileSubContainer = styled(SubContainer)`
  display: none;
  @media (max-width: ${bp.mobileMax}) {
    display: inherit;
  }
`

const SelectorWrapper = styled.div<{ isInverted?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  margin: calc((${nav.mobile} * 1.3) - 1em) ${padding.mobile} 0 ${padding.mobile};

  button {
    font-size: 1rem;
    font-weight: 700;
    font-family: inherit;
    border: 2px solid ${colors.gray};
    margin-right: 0;
    margin-top: 1em;
    height: 3em;
    color: ${props => (props.isInverted ? colors.white : colors.dark)};
    width: auto;

    &:first-child,
    &:nth-child(2) {
      margin-right: 1em;
    }
  }

  @media (min-width: ${bp.tabletMin}) {
    margin: calc(${nav.desktop} * 1.2) ${padding.desktop} 0 ${padding.desktop};
  }

  @media (min-width: ${bp.desktopMin}) {
    margin: 0 0;

    button {
      margin-top: 0;
      font-size: inherit;
      border: none;
      flex: 0;

      &:first-child,
      &:nth-child(2) {
        margin-right: 0;
      }
    }
  }
`

const Links = styled.div<{ isInverted?: boolean }>`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;

  a {
    margin: 0 15px;
    height: calc(${nav.desktop} - 8px);
    border-top: 6px solid transparent;
    border-bottom: 4px solid transparent;
    font-weight: 500;
    color: ${props => (props.isInverted ? "rgba(255, 255, 255, 0.6)" : colors.gray60)};
    display: flex;

    div {
      margin: auto;
    }
    &.active {
      border-top-color: ${colors.black};
      color: ${colors.dark};
      font-weight: 600;

      ${props =>
        props.isInverted
          ? `
            border-top-color: ${colors.white};
            color: ${colors.white};
          `
          : ""}
    }
  }
`

const Scrim = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(230, 245, 245, 0.9);
  z-index: 2;

  display: none;
  @media (min-width: ${bp.tabletMin}) and (max-width: ${bp.tabletMax}) {
    display: block;
  }
`

const ButtonIcon = styled(Icon)`
  vertical-align: middle;
  margin-left: 1.1875em;
`

/**
 * Mobile Menu & buttons
 */
const MobileMenu = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  overflow: hidden;
  z-index: 3;

  width: ${props => (props.isOpen ? "100%;" : "0px;")};
  transition: width 0.1s;

  @media (min-width: ${bp.tabletMin}) {
    width: ${props => (props.isOpen ? "75%;" : "0px;")};
    box-shadow: 0px 0px 32px -8px rgba(0, 30, 45, 0.2);
  }

  @media (min-width: ${bp.desktopMin}) {
    display: none;
  }
`

const MenuToggle = styled.button`
  width: 48px;
  height: 48px;
  border: none;
  background-color: transparent;

  &:focus {
    outline: none;
  }
`

const MobileLinks = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  font-weight: 500;
  text-align: left;
  font-size: 1.125rem;
  margin: ${padding.mobile};
  @media (min-width: ${bp.tabletMin}) {
    margin: ${padding.desktop};
  }

  a {
    margin: 0.888em 0;
    padding-left: 1rem;
    border-left: 4px solid transparent;
    color: ${colors.gray60};
    font-size: 1.125rem;
    font-weight: 500;
    background: none;
    line-height: 32px;

    &:hover {
      background: none;
    }

    &.active {
      border-left-color: ${colors.black};
      color: ${colors.dark};
      font-weight: 600;
    }

    @media (min-width: ${bp.tabletMin}) {
      padding-left: 1.5rem;
      font-size: 2rem;
    }
  }
`

const MobileButtons = styled.div`
  margin: ${padding.mobile};

  button {
    width: 100%;
    margin-bottom: 1rem;
    height: 3.5rem;
    font-family: inherit;
  }

  @media (min-width: ${bp.tabletMin}) {
    margin: ${padding.desktop};

    button {
      font-size: 1.125rem;
    }
  }
`

/**
 * Desktop Menu & buttons
 */
const DesktopMenuToggle = styled(MenuToggle)`
  margin-left: 2.875em;
`

const DesktopMenu = styled(Container)<{ isOpen?: boolean; isInverted?: boolean }>`
  top: calc(${nav.desktop} - 1px);
  display: none;
  flex-direction: column;
  overflow: hidden;
  align-items: flex-end;
  justify-content: center;
  background-color: ${props => (props.isInverted ? colors.dark : colors.white)};

  height: ${props => (props.isOpen ? `calc(0.75 * ${nav.desktop})` : "0;")};
  transition: height 0.1s;

  @media (min-width: ${bp.desktopMin}) {
    display: flex;
  }
`

const DesktopButtons = styled.div<{ isInverted?: boolean }>`
  display: flex;
  align-items: center;

  > * {
    position: relative;

    &:before {
      content: "";
      display: block;
      position: absolute;
      left: 0;
      top: 23%;
      bottom: 23%;
      width: 1px;
      background-color: ${props =>
        props.isInverted ? colors.grayLight10 : hexToRgba(colors.black, 0.12)};
    }

    &:first-child:before {
      display: none;
    }
  }

  button {
    font-size: inherit;
    font-family: inherit;
  }
`

const DesktopMenuDivider = styled.div<{ isInverted?: boolean }>`
  position: absolute;
  top: 0;
  left: ${padding.desktop};
  right: ${padding.desktop};
  height: 2px;
  background-color: ${props => (props.isInverted ? colors.grayLight10 : colors.gray10)};
`

const MenuButton = styled(Button)`
  font-weight: 500;
  height: 2.5em;
  line-height: 2.5em;
  padding: 0 1.25em;
  font-weight: 700;

  @media (min-width: ${bp.desktopMin}) {
    border: none;
    font-weight: 500;
  }
`

const StyledOrderButton = styled(Button)`
  font-weight: 700;
  font-size: 1rem;
  padding: 0;

  @media (min-width: ${bp.desktopMin}) {
    font-size: 0.875rem;
    padding: 0.7em 1.25em;
  }
`

const DownloadLinks = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  @media (min-width: ${bp.desktopMin}) {
    flex-direction: row;
  }
  button {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    @media (min-width: ${bp.desktopMin}) {
      margin-top: 40px;
      margin-bottom: 40px;
    }
  }
`

const ModalDescription = styled.div`
  margin-bottom: 1rem;
  p {
    color: ${colors.black};
    margin-bottom: 0;
  }
`

const ModalNextSteps = styled.div`
  p {
    margin: 0;
    a {
      color: ${colors.black};
      border-bottom: 1px solid ${colors.black};
      padding-bottom: 1px;
    }
  }
`

const Nav = ({ activeTab, isInverted, data }: NavProps) => {
  const locale = useContext(LocaleContext)
  const { navigationData } = data
  const navigation = navigationData.nodes.find(item => item.node_locale === locale)
  const langaugeSelectorContent = (navigationData?.nodes.find(
    item => item.languageModalSection
  ) as ContentfulNavigation)?.languageModalSection
  const currencySelectorContent = (navigationData?.nodes.find(
    item => item.currencyModalSection
  ) as ContentfulNavigation)?.currencyModalSection
  const downloadModal = navigation?.downloadModalSection
  const [isOnTop, setIsOnTop] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [isDownloadModalOpen, setDownloadModalOpen] = useState(false)

  useScrollPosition(
    ({ currPos }) => {
      setIsOnTop(currPos.y === 0)
    },
    undefined,
    undefined,
    true,
    50
  )

  const toDownload = () => {
    const isAndroid: boolean = /(android)/i.test(navigator.userAgent) && !window.MSStream
    const isIos: boolean = /iPad|iPhone/.test(navigator.userAgent) && !window.MSStream
    if (isAndroid) {
      return window.location.assign(
        "https://play.google.com/store/apps/details?id=com.hmdglobal.app.hydra"
      )
    } else if (isIos) {
      return window.location.assign(
        "https://apps.apple.com/us/app/hmd-connect/id1508102559?ls=1itms-apps://itunes.apple.com/app/apple-store/id1508102559?mt=8"
      )
    } else {
      return setDownloadModalOpen(true)
    }
  }

  const links = navigation?.links?.map(item =>
    item?.url?.startsWith("http") ? (
      <a key={item?.heading!} href={item?.url} target="_blank" data-gtm-cta={item?.gtm}>
        <div>{item?.heading}</div>
      </a>
    ) : (
      <Link
        key={item?.heading!}
        className={clsx("", { active: item?.heading === activeTab })}
        to={createLocalizedPath(locale, item?.url!)}
        data-gtm-cta={item?.gtm}
        onClick={() => setIsOpen(false)}
      >
        <div>{item?.heading}</div>
      </Link>
    )
  )

  const SignInButton = ({ isMobile }: ChangesOnMobile) => {
    const props = {
      onClick: () => window.location.assign(`${navigation?.signInLink?.url}`),
      "data-gtm-cta": (navigation?.signInLink?.gtm || "").toString(),
    }

    return isMobile ? (
      <ExternalLinkButton transparent {...props}>
        <div>{navigation?.signInLink?.heading}</div>
      </ExternalLinkButton>
    ) : (
      <MenuButton transparent inverted {...props}>
        {navigation?.signInLink?.heading}
        <ButtonIcon icon="sign-in" />
      </MenuButton>
    )
  }

  const DownloadButton = ({ isMobile }: ChangesOnMobile) => (
    <MenuButton
      type="button"
      transparent
      onClick={() => toDownload()}
      data-gtm-cta={navigation?.downloadButton?.gtm!}
    >
      {navigation?.downloadButton?.heading!}
      {!isMobile && <ButtonIcon icon="download" />}
    </MenuButton>
  )

  const OrderButton = () => (
    <StyledOrderButton
      type="button"
      inverted={isInverted && isOnTop}
      data-gtm-cta={(navigation?.getSimButton?.gtm || "").toString()}
      onClick={() => navigate(createLocalizedPath(locale, navigation?.getSimButton?.url!))}
    >
      {navigation?.getSimButton?.heading}
    </StyledOrderButton>
  )

  const DesktopMenuBar = ({ isFull }: DesktopMenuBarProps) => (
    <DesktopMenuToggle
      className={`menu ${isFull ? "is-open" : ""}`}
      type="button"
      onClick={() => setIsOpen(!isOpen)}
      data-gtm-cta="mobile-menu-open"
    >
      {isFull ? (
        <Icon
          icon="close"
          width="36px"
          height="36px"
          color={isInverted && isOnTop && !isOpen ? colors.white : colors.dark}
        />
      ) : (
        <Icon
          icon="hamburger-menu"
          width="36px"
          height="36px"
          transform="scale(-1,1)"
          fill={isInverted && isOnTop && !isOpen ? colors.white : colors.dark}
        />
      )}
    </DesktopMenuToggle>
  )

  const MobileMenuBar = ({ isFull }: MobileMenuBarProps) => (
    <>
      <SubContainer justifyContent="flex-start">
        <MenuToggle
          className={clsx("menu", { "is-open": isFull })}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          data-gtm-cta="mobile-menu-open"
        >
          {isFull ? (
            <Icon icon="close" width="36px" height="36px" color={colors.teal} />
          ) : (
            <Icon
              icon="hamburger-menu"
              width="36px"
              height="36px"
              fill={isInverted && isOnTop ? colors.white : colors.black}
            />
          )}
        </MenuToggle>
      </SubContainer>
      <TabletSubContainer stretch justifyContent={isFull ? "flex-end" : undefined}>
        <HmdConnectLogo isInverted={isInverted && isOnTop && !isFull} />
      </TabletSubContainer>
      {!isFull && <TabletSubContainer />}
      <MobileSubContainer stretch justifyContent="flex-end">
        <HmdConnectLogo isSmall isInverted={isInverted && isOnTop && !isFull} />
      </MobileSubContainer>
    </>
  )

  const DownloadModal = () => {
    const modalHeading = (downloadModal?.heading as ContentfulHeading).heading!
    const modalDescription = downloadModal?.description as ParagraphWithRemarkFragment
    const modalSectionFields = downloadModal?.contentfulfields
    const modalSectionFieldsLength = modalSectionFields!.length
    const nextSteps =
      modalSectionFields &&
      (modalSectionFields[modalSectionFieldsLength - 1] as ParagraphWithRemarkFragment)

    return (
      <BaseModal
        isOpen={isDownloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        header={modalHeading}
        modalStatus={ModalStatus.DOWNLOAD}
      >
        <ModalDescription>
          <Paragraph isSemiLarge content={modalDescription} />
        </ModalDescription>
        <DownloadLinks>
          {(downloadModal?.contentfulfields as ContentfulLink[])?.map(item => (
            <>
              {item.preset === "google-play" && (
                <GooglePlayCta
                  key={item?.gtm!}
                  url={item?.url!}
                  gtm={item?.gtm!}
                  content={item?.heading!}
                />
              )}
              {item.preset === "app-store" && (
                <AppStoreCta
                  key={item?.gtm!}
                  url={item?.url!}
                  gtm={item?.gtm!}
                  content={item?.heading!}
                />
              )}
              {item.preset === "web-app" && (
                <WebStoreCta
                  key={item?.gtm!}
                  url={item?.url!}
                  gtm={item?.gtm!}
                  content={item?.heading!}
                />
              )}
            </>
          ))}
        </DownloadLinks>
        <ModalNextSteps>{nextSteps && <Paragraph content={nextSteps} />}</ModalNextSteps>
      </BaseModal>
    )
  }

  return (
    <>
      <DesktopContainer isTransparent={isOnTop && !isOpen} isInverted={isInverted && !isOpen}>
        <SubContainer justifyContent="flex-start">
          <HmdConnectLogo isInverted={isInverted && isOnTop && !isOpen} />
        </SubContainer>
        <SubContainer stretch>
          <Links isInverted={isInverted && isOnTop && !isOpen}>{links}</Links>
        </SubContainer>
        <SubContainer justifyContent="flex-end" fixedWidth={"auto"}>
          <OrderButton />
        </SubContainer>
        <DesktopMenuBar isFull={isOpen} />
        <DesktopMenu isOpen={isOpen} isInverted={isInverted && isOnTop && !isOpen}>
          <DesktopMenuDivider isInverted={isInverted && isOnTop && !isOpen} />
          <DesktopButtons isInverted={isInverted && isOnTop && !isOpen}>
            {langaugeSelectorContent && currencySelectorContent && (
              <SelectorWrapper isInverted={isInverted && isOnTop && !isOpen}>
                <LanguageSelector contents={langaugeSelectorContent} />
                <CurrencySelector contents={currencySelectorContent} />
              </SelectorWrapper>
            )}
            <DownloadButton />
            <DownloadModal />
            <SignInButton />
          </DesktopButtons>
        </DesktopMenu>
      </DesktopContainer>
      <MobileContainer isTransparent={isOnTop} isInverted={isInverted}>
        <MobileMenuBar isFull={false} />
        {isOpen && <Scrim />}
        <MobileMenu isOpen={isOpen}>
          <MobileContainer absolute>
            <MobileMenuBar isFull />
          </MobileContainer>
          {langaugeSelectorContent && currencySelectorContent && (
            <SelectorWrapper>
              <LanguageSelector contents={langaugeSelectorContent} />
              <CurrencySelector contents={currencySelectorContent} />
            </SelectorWrapper>
          )}
          <MobileLinks>
            {links}
            <SignInButton isMobile />
          </MobileLinks>
          <MobileButtons>
            <OrderButton />
            <DownloadButton isMobile />
          </MobileButtons>
        </MobileMenu>
      </MobileContainer>
    </>
  )
}

export default (props: Props) => (
  <StaticQuery
    query={graphql`
      query Nav {
        navigationData: allContentfulNavigation {
          nodes {
            links {
              ...Link
            }
            signInLink {
              heading
              url
              gtm
            }
            getSimButton {
              ...Link
            }
            downloadButton {
              ...Link
            }
            downloadModalSection {
              ... on ContentfulSection {
                heading {
                  heading
                }
                description {
                  ...ParagraphWithRemark
                }
                contentfulfields {
                  ... on ContentfulParagraph {
                    ...ParagraphWithRemark
                  }
                  ... on ContentfulLink {
                    preset
                    heading
                    url
                  }
                }
              }
            }
            languageModalSection {
              ... on ContentfulSection {
                heading {
                  heading
                }
              }
              contentfulfields {
                ... on ContentfulLanguageList {
                  localeName
                  languageName
                  localeCode
                }
              }
            }
            currencyModalSection {
              ... on ContentfulSection {
                heading {
                  heading
                }
              }
              contentfulfields {
                ... on ContentfulCurrencyList {
                  currencyHeading
                  currencyName
                  currencySymbol
                }
              }
            }
            node_locale
          }
        }
      }
    `}
    render={data => <Nav data={data} {...props} />}
  />
)
